import { useAbility } from '@casl/react';
/* eslint-disable @typescript-eslint/no-var-requires */
import { Email, Language, Phone } from '@mui/icons-material';
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Grid,
  Link,
  Typography,
  useTheme,
} from '@mui/material';
import L, { LatLngExpression } from 'leaflet';

import 'leaflet/dist/leaflet.css';
import { useEffect, useMemo, useRef, useState } from 'react';
import { MapContainer, Marker, Popup, TileLayer, useMap } from 'react-leaflet';
import { ACTIONS } from 'src/auth/utils/defineAbilityFor';
import { AbilityContext } from 'src/context/canContext';
import { useAdminUrlNavigation } from 'src/hooks/useAdminUrlNavigation';
import { useGetAllApplicationGroupsQuery } from 'src/services/switch-rest-api/application-group.service';
import { formatDate } from 'src/utils/dateHelper';
import { ApplicationGroup } from 'switch-shared/modules/crud/application-group/application-group.entity';
import { getImageURL } from 'switch-shared/utils/s3.utils';

export const GROUPS_NAVIGATE_EVENT = 'navigate-to-group-location';
export const GROUPS_RESET_BOUNDS_EVENT = 'reset-groups-map-bounds';

interface NavigateToGroupLocationEvent extends CustomEvent {
  detail: {
    coordinates: { latitude: number; longitude: number };
    zoom: number;
    itemId: string;
    itemName: string;
    openPopup?: boolean;
  };
}

interface GroupWithCoordinates extends ApplicationGroup {
  clientDetails?: ApplicationGroup['clientDetails'] & {
    address?: {
      coordinates?: number[];
      address?: string | null;
    };
  };
}

type GroupCoordinateEntry = GroupWithCoordinates & {
  coordinates: [number, number];
};

const groupMarkerIcon = new L.Icon({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
  iconSize: new L.Point(25, 41),
  iconAnchor: new L.Point(12, 41),
  shadowSize: new L.Point(41, 41),
  popupAnchor: new L.Point(1, -34),
});

const MapNavigationHandler = ({
  onNavigate,
}: {
  onNavigate: (itemId: string, openPopup?: boolean) => void;
}) => {
  const map = useMap();

  useEffect(() => {
    const handleNavigateToLocation = (event: Event) => {
      const customEvent = event as NavigateToGroupLocationEvent;
      const { coordinates, zoom, itemId, openPopup } = customEvent.detail;

      if (coordinates) {
        // Close any open popup immediately before starting navigation
        onNavigate(itemId, false);

        // Calculate offset to center the marker with popup visible
        const targetPoint = map.project(
          [coordinates.latitude, coordinates.longitude] as LatLngExpression,
          zoom
        );
        // Offset by 100 pixels down to account for popup height
        targetPoint.y -= 200;
        const targetLatLng = map.unproject(targetPoint, zoom);

        map.flyTo(targetLatLng, zoom, {
          duration: 1.5,
          easeLinearity: 0.5,
        });

        if (openPopup && itemId) {
          // Wait for the map to finish moving before opening popup
          map.once('moveend', () => {
            onNavigate(itemId, true);
          });
        }
      }
    };

    window.addEventListener(GROUPS_NAVIGATE_EVENT, handleNavigateToLocation);

    return () => {
      window.removeEventListener(
        GROUPS_NAVIGATE_EVENT,
        handleNavigateToLocation
      );
    };
  }, [map, onNavigate]);

  return null;
};

const MapResizeHandler = () => {
  const map = useMap();

  useEffect(() => {
    const container = map.getContainer();
    const resizeObserver = new ResizeObserver(() => {
      map.invalidateSize();
    });

    resizeObserver.observe(container);
    return () => resizeObserver.disconnect();
  }, [map]);

  return null;
};

const MapBoundsHandler = ({
  groups,
  isDataLoaded = false,
  onResetBounds,
}: {
  groups: Array<{ coordinates: [number, number] }>;
  isDataLoaded?: boolean;
  onResetBounds?: () => void;
}) => {
  const map = useMap();
  const hasInitialFit = useRef(false);

  const fitBoundsToGroups = () => {
    if (!groups.length) return;

    const bounds = L.latLngBounds(
      groups.map((group) => group.coordinates as unknown as LatLngExpression)
    );
    map.fitBounds(bounds, {
      padding: [50, 50],
      maxZoom: 10,
      animate: true,
      duration: 1.5,
    });
  };

  useEffect(() => {
    if (!isDataLoaded || hasInitialFit.current) {
      return;
    }

    fitBoundsToGroups();
    hasInitialFit.current = true;
  }, [isDataLoaded, map]);

  useEffect(() => {
    const handleResetBounds = () => {
      onResetBounds?.();
      fitBoundsToGroups();
    };
    window.addEventListener(GROUPS_RESET_BOUNDS_EVENT, handleResetBounds);
    return () =>
      window.removeEventListener(GROUPS_RESET_BOUNDS_EVENT, handleResetBounds);
  }, [map, groups, onResetBounds]);

  return null;
};

const FALLBACK_CENTER: LatLngExpression = [-28.739154, 24.758499];

const GroupsMap = () => {
  const ability = useAbility(AbilityContext);
  const canReadGroups = ability.can(ACTIONS.READ, 'ApplicationGroups');
  const { navigateToApplicationGroup } = useAdminUrlNavigation();
  const theme = useTheme();
  const [openPopupId, setOpenPopupId] = useState<string | null>(null);
  const markerRefs = useRef<{ [key: string]: L.Marker | null }>({});

  const { data, isLoading, isFetching, isError } =
    useGetAllApplicationGroupsQuery(
      {
        limit: 0,
        orderByField: 'name',
      },
      {
        skip: !canReadGroups,
      }
    );

  const groupsWithCoordinates = useMemo<GroupCoordinateEntry[]>(() => {
    const groups = (data?.data ?? []) as GroupWithCoordinates[];
    return groups
      .filter(
        (group) =>
          group.clientDetails?.address?.coordinates &&
          group.clientDetails.address.coordinates.length === 2
      )
      .map((group) => {
        const [longitude, latitude] =
          group.clientDetails!.address!.coordinates!;
        return {
          ...group,
          coordinates: [latitude, longitude],
        };
      });
  }, [data]);

  const getCenterCoords = (): LatLngExpression => {
    if (!groupsWithCoordinates.length) {
      return FALLBACK_CENTER;
    }

    const sumCoordinates = groupsWithCoordinates.reduce(
      (acc, group) => {
        const [lat, lng] = group.coordinates;
        return {
          lat: acc.lat + lat,
          lng: acc.lng + lng,
        };
      },
      { lat: 0, lng: 0 }
    );

    return [
      sumCoordinates.lat / groupsWithCoordinates.length,
      sumCoordinates.lng / groupsWithCoordinates.length,
    ] as LatLngExpression;
  };

  const handleNavigateToGroup = (groupId: string) => {
    navigateToApplicationGroup(groupId);
  };

  const handleNavigate = (itemId: string, openPopup?: boolean) => {
    if (openPopup) {
      setOpenPopupId(itemId);
      // Open the popup using the marker ref
      const marker = markerRefs.current[itemId];
      if (marker) {
        marker.openPopup();
      }
    } else {
      // Close any currently open popup
      if (openPopupId && markerRefs.current[openPopupId]) {
        markerRefs.current[openPopupId]?.closePopup();
      }
      setOpenPopupId(null);
    }
  };

  const handleResetBounds = () => {
    // Close any open popup
    if (openPopupId && markerRefs.current[openPopupId]) {
      markerRefs.current[openPopupId]?.closePopup();
    }
    setOpenPopupId(null);
  };

  const isDataLoaded = !isLoading && !isFetching;

  if (!canReadGroups) {
    return (
      <Grid
        container
        width='100%'
        height='100%'
        justifyContent='center'
        alignItems='center'
        px={2}
      >
        <Grid item>
          <Typography variant='body2' color='text.secondary' textAlign='center'>
            You do not have permission to view application groups.
          </Typography>
        </Grid>
      </Grid>
    );
  }

  if (isLoading || isFetching) {
    return (
      <Grid
        container
        width='100%'
        height='100%'
        justifyContent='center'
        alignItems='center'
      >
        <Grid item>
          <CircularProgress />
        </Grid>
      </Grid>
    );
  }

  if (isError || groupsWithCoordinates.length === 0) {
    return (
      <Grid
        container
        width='100%'
        height='100%'
        justifyContent='center'
        alignItems='center'
        px={2}
      >
        <Grid item>
          <Typography variant='body2' color='text.secondary' textAlign='center'>
            {isError
              ? 'Unable to load group locations at the moment.'
              : 'No application groups with coordinates found.'}
          </Typography>
        </Grid>
      </Grid>
    );
  }

  return (
    <MapContainer
      center={getCenterCoords()}
      zoom={5}
      scrollWheelZoom
      style={{ height: '100%', width: '100%' }}
    >
      <MapNavigationHandler onNavigate={handleNavigate} />
      <MapResizeHandler />
      <MapBoundsHandler
        groups={groupsWithCoordinates}
        isDataLoaded={isDataLoaded}
        onResetBounds={handleResetBounds}
      />
      <TileLayer
        attribution='&copy; <a href="https://openstreetmap.org/copyright">OpenStreetMap contributors</a>'
        url='https://tile.openstreetmap.org/{z}/{x}/{y}.png'
      />
      {groupsWithCoordinates.map((group) => (
        <Marker
          key={group._id}
          position={group.coordinates as unknown as LatLngExpression}
          title={group.name}
          icon={groupMarkerIcon}
          ref={(ref) => {
            markerRefs.current[group._id] = ref;
          }}
        >
          <Popup>
            <Box sx={{ minWidth: 360, maxWidth: 450 }}>
              {/* Logo */}
              {group.brand?.assets?.logo ? (
                <Box sx={{ mb: 2 }}>
                  <img
                    src={
                      getImageURL(
                        group.brand.assets.logo.key,
                        group.brand.assets.logo.bucket
                      ) || ''
                    }
                    alt={`${group.name} logo`}
                    style={{
                      maxWidth: '150px',
                      maxHeight: '60px',
                      objectFit: 'contain',
                    }}
                  />
                </Box>
              ) : (
                <Typography variant='subtitle2' fontWeight={600} mb={1}>
                  {group.name}
                </Typography>
              )}

              <Typography variant='body2' color='text.secondary' mb={1.5}>
                {group.clientDetails?.address?.address ?? 'No address provided'}
              </Typography>

              {/* SLA Details */}
              {group.serviceLevelAgreementDetails && (
                <>
                  <Divider sx={{ my: 1.5 }} />
                  <Typography
                    variant='caption'
                    fontWeight={600}
                    color='text.secondary'
                    mb={1}
                    display='block'
                  >
                    Service Level Agreement
                  </Typography>
                  <Box sx={{ mb: 1 }}>
                    <Typography
                      variant='body2'
                      color='text.secondary'
                      sx={{ mb: '4px !important' }}
                    >
                      <strong>Period:</strong>{' '}
                      {formatDate(
                        group.serviceLevelAgreementDetails.startTimeISO
                      )}{' '}
                      -{' '}
                      {formatDate(
                        group.serviceLevelAgreementDetails.endTimeISO
                      )}
                    </Typography>
                    <Typography
                      variant='body2'
                      color='text.secondary'
                      sx={{ mt: '4px !important' }}
                    >
                      <strong>Status:</strong>{' '}
                      {group.serviceLevelAgreementDetails.signedPdfKey ? (
                        <span style={{ color: theme.palette.success.main }}>
                          Signed
                        </span>
                      ) : (
                        <span style={{ color: theme.palette.warning.main }}>
                          Not Signed
                        </span>
                      )}
                    </Typography>
                  </Box>
                </>
              )}

              {/* Website Link */}
              {group.clientDetails?.website && (
                <Box
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}
                >
                  <Language fontSize='small' color='action' />
                  <Link
                    href={group.clientDetails.website}
                    target='_blank'
                    rel='noopener noreferrer'
                    variant='body2'
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {group.clientDetails.website}
                  </Link>
                </Box>
              )}

              {/* Contact Details */}
              {!!group.clientDetails?.contactDetails?.length && (
                <>
                  <Divider sx={{ my: 1.5 }} />

                  <Typography
                    variant='caption'
                    fontWeight={600}
                    color='text.secondary'
                    mb={1}
                    display='block'
                  >
                    Contact Details
                  </Typography>

                  <Grid container spacing={2}>
                    {group.clientDetails.contactDetails.map(
                      (contact, index) => {
                        if (
                          !contact.name &&
                          !contact.surname &&
                          !contact.email &&
                          !contact.phone
                        ) {
                          return null;
                        }
                        return (
                          <Grid
                            item
                            xs={
                              group.clientDetails!.contactDetails!.length > 1
                                ? 6
                                : 12
                            }
                            key={index}
                          >
                            <Box sx={{ mb: 1 }}>
                              {contact.type && (
                                <Typography
                                  variant='caption'
                                  color='text.secondary'
                                  display='block'
                                  sx={{ textTransform: 'capitalize' }}
                                >
                                  {contact.type}
                                </Typography>
                              )}
                              {(contact.name || contact.surname) && (
                                <Typography
                                  variant='body2'
                                  fontWeight={500}
                                  sx={{
                                    my: `${theme.tokens.spacing.sm}px !important`,
                                  }}
                                >
                                  {[contact.name, contact.surname]
                                    .filter(Boolean)
                                    .join(' ')}
                                </Typography>
                              )}
                              {contact.email && (
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                    mt: 0.5,
                                  }}
                                >
                                  <Email
                                    fontSize='small'
                                    sx={{ fontSize: 14 }}
                                    color='action'
                                  />
                                  <Link
                                    href={`mailto:${contact.email}`}
                                    variant='body2'
                                    sx={{
                                      fontSize: '0.8rem',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                    }}
                                  >
                                    {contact.email}
                                  </Link>
                                </Box>
                              )}
                              {contact.phone && (
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                    mt: 0.5,
                                  }}
                                >
                                  <Phone
                                    fontSize='small'
                                    sx={{ fontSize: 14 }}
                                    color='action'
                                  />
                                  <Link
                                    href={`tel:${contact.phone}`}
                                    variant='body2'
                                    sx={{ fontSize: '0.8rem' }}
                                  >
                                    {contact.phone}
                                  </Link>
                                </Box>
                              )}
                            </Box>
                          </Grid>
                        );
                      }
                    )}
                  </Grid>
                </>
              )}

              <Button
                variant='contained'
                size='small'
                fullWidth
                onClick={() => handleNavigateToGroup(group._id)}
                sx={{ mt: 2 }}
              >
                View group
              </Button>
            </Box>
          </Popup>
        </Marker>
      ))}
    </MapContainer>
  );
};

export default GroupsMap;
